import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { createEntityAdapter, EntityState } from '@reduxjs/toolkit';

import { getTrades, Trade, TradesResp } from '#/api/trades';
import { TradeNotification } from '#/api/ws/ws';
import { Market } from '#/api/markets';

import { useTradeActiveMarketStoreView } from '#/features/perpetuals/active-market-context';

import createSafeContext from '#/utils/createSafeContext';
import useLiveConnection, {
  LiveConnectionBaseState,
} from '#/utils/useLiveConnection';

const adapter = createEntityAdapter<Trade>({
  sortComparer: (tradeA, tradeB) =>
    tradeB.id.localeCompare(tradeA.id, undefined, { numeric: true }),
});
const selectors = adapter.getSelectors();

export const removeOldest = (state: EntityState<Trade>): EntityState<Trade> => {
  const lastTradeIdx = state.ids[state.ids.length - 1]!;
  return adapter.removeOne(state, lastTradeIdx);
};

const TRADES_LIMIT = 100;

const INITIAL_STATE: TradesStoreState = {
  status: 'connected',
  loadRetryCount: 0,
  error: '',
  trades: adapter.getInitialState(),
};

export interface TradesStoreState extends LiveConnectionBaseState {
  readonly trades: EntityState<Trade>;
}

export interface TradesStoreView extends LiveConnectionBaseState {
  readonly getAllTrades: () => readonly Trade[];
  readonly getLatestTrade: () => Trade | undefined;
}

const [useTradesStoreView, TradesStoreViewProvider] =
  createSafeContext<TradesStoreView>('TradesStoreView');

export type { TradesStoreView as TradesStore };
export { useTradesStoreView as useTradesStore };

interface ProviderProps {
  readonly children: ReactNode;
}

export function TradesStoreProvider(props: ProviderProps) {
  const [store, setStore] = useState(INITIAL_STATE);
  const { activeMarket } = useTradeActiveMarketStoreView();

  const storeView = useMemo(() => createStoreView(store), [store]);

  useTradesLiveConnection(setStore, activeMarket);

  return (
    <TradesStoreViewProvider value={storeView}>
      {props.children}
    </TradesStoreViewProvider>
  );
}

export function createStoreView(store: TradesStoreState): TradesStoreView {
  return {
    status: store.status,
    loadRetryCount: store.loadRetryCount,
    error: store.error,
    getAllTrades: () => selectors.selectAll(store.trades),
    getLatestTrade: () => {
      const latestTradeId = selectors.selectIds(store.trades)[0];
      if (latestTradeId == null) return undefined;
      return selectors.selectById(store.trades, latestTradeId);
    },
  };
}

function useTradesLiveConnection(
  setState: Dispatch<SetStateAction<TradesStoreState>>,
  activeMarket: Market | null,
): void {
  const isReady = activeMarket != null;
  const channel = isReady ? `trades.${activeMarket.symbol}` : '';

  const fetchAll = useCallback(
    async (signal: AbortSignal) =>
      getTrades({
        signal,
        market: activeMarket!.symbol,
      }),
    [activeMarket],
  );

  const setAll = useCallback(
    (data: TradesResp) => {
      setState((state) => ({
        ...state,
        trades: adapter.setAll(state.trades, data.results),
      }));
    },
    [setState],
  );

  const resetAll = useCallback(() => {
    setState((state) => ({
      ...state,
      trades: adapter.removeAll(state.trades),
    }));
  }, [setState]);

  const applyWSNotification = useCallback(
    (notif: TradeNotification) => {
      setState((state) => {
        let newTrades = adapter.setOne(state.trades, notif.data);

        if (selectors.selectTotal(newTrades) > TRADES_LIMIT) {
          newTrades = removeOldest(newTrades);
        }

        return {
          ...state,
          trades: newTrades,
        };
      });
    },
    [setState],
  );

  useLiveConnection<TradesResp, TradesStoreState, TradeNotification>({
    setState,
    channel,
    isReady,
    fetchAll,
    setAll,
    resetAll,
    applyWSNotification,
  });
}
